import { Cookies, Platform } from 'quasar'
import { $fetch } from '@/boot/fetch'
import { replacePathTokens } from '@/utils/routes.util'
import { PEG_RETAILER_AUTH_COOKIE } from '@/constants/cookies.const'

const ENDPOINT = '/retailers/{{retailerId}}/product-tags'

export default {
  /**
   * PEG-967: tag a retailer-supplied product list on Shopify.
   *
   * Form-driven equivalent of the onboarding chat flow's product tagging:
   * the retailer pastes/uploads a list of products and Peg applies the given
   * tag(s) — e.g. their NotReturnable (1.b) or ReplacementParts (1.c) tag — to
   * each. Tagging is synchronous; the resolved data carries per-product
   * results. Throws on non-2xx (404 no connected shop, 403 missing Shopify
   * scope, 400 unparseable list) — callers use try/catch.
   *
   * @param {String} retailerId - The retailer ID
   * @param {Object} payload
   * @param {String} payload.content - Raw product list (one identifier per line, or CSV/TSV)
   * @param {('ID'|'HANDLE'|'TITLE'|'SKU')} [payload.identifierType] - How to interpret each line (default TITLE)
   * @param {String[]} payload.tags - The tag(s) to apply to every parsed product
   * @returns {Promise<{parsedCount:number,tagged:number,failed:number,results:Array}>}
   */
  create(retailerId, { content, identifierType, tags }) {
    return $fetch.peg(replacePathTokens(ENDPOINT, { retailerId }), {
      method: 'POST',
      body: { content, identifierType, tags },
    })
  },

  /**
   * PEG-967: download the .xlsx product-list upload template. The backend streams
   * a binary file, so this bypasses the JSON `$fetch.peg` wrapper and reads the
   * response as a Blob (same pattern as the routing-template download). Returns
   * `{ blob, filename }` for the caller to trigger a browser download.
   *
   * `kind` (NON_RETURNABLE | REPLACEMENT_PARTS | RETURN_TO_RETAILER |
   * RETURN_TO_VENDOR) tailors the server filename/header note to the list being
   * filled; the filename is read back from the response's Content-Disposition so
   * the download matches whatever the server named it.
   *
   * @param {string} retailerId
   * @param {string} [kind] - Product-list kind from the upload block
   * @returns {Promise<{ blob: Blob, filename: string }>}
   */
  async downloadTemplate(retailerId, kind) {
    const query = kind ? `?kind=${encodeURIComponent(kind)}` : ''
    const url = `${import.meta.env.VITE_PEG_API_URL}${replacePathTokens(ENDPOINT, { retailerId })}/template${query}`
    const headers = {
      'x-app-id': import.meta.env.VITE_PEG_API_APP_ID,
      'x-platform': Platform.is.nativeMobile ? 'native' : 'web',
      'ngrok-skip-browser-warning': 'true',
      'x-access-token': Cookies.get(PEG_RETAILER_AUTH_COOKIE) || '',
    }
    const res = await fetch(url, { method: 'GET', headers, credentials: 'include' })
    if (!res.ok) {
      let message = `Failed to download template (HTTP ${res.status})`
      try {
        const body = await res.json()
        if (body?.message) message = body.message
      } catch {
        // non-JSON error body — keep the default message
      }
      const error = new Error(message)
      error.statusCode = res.status
      throw error
    }
    const blob = await res.blob()
    const disposition = res.headers.get('Content-Disposition') || ''
    const match = disposition.match(/filename="?([^"]+)"?/i)
    const filename = match ? match[1] : 'peg-product-list-template.xlsx'
    return { blob, filename }
  },
}
